import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Send, Share2, AlertCircle, MessageSquare } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'twitter', name: 'Twitter/X', icon: '𝕏' },
  { id: 'facebook', name: 'Facebook', icon: 'f' },
  { id: 'instagram', name: 'Instagram', icon: '📷' },
  { id: 'tiktok', name: 'TikTok', icon: '♪' },
  { id: 'whatsapp', name: 'WhatsApp', icon: '💬' },
];

export const AdminSocialPage = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  const [content, setContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedBook, setSelectedBook] = useState('');
  
  // WhatsApp direct message
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const fetchData = async () => {
    try {
      const [postsRes, booksRes] = await Promise.all([
        axios.get(`${API}/admin/social-posts`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/books?limit=50`, { headers: getHeaders(), withCredentials: true })
      ]);
      setPosts(postsRes.data.posts || []);
      setBooks(booksRes.data.books || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('Please enter content');
      return;
    }
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    setPosting(true);
    try {
      const response = await axios.post(`${API}/admin/social-post`, {
        content,
        platforms: selectedPlatforms,
        book_id: selectedBook || null
      }, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      if (response.data.mocked_platforms?.length > 0) {
        toast.success(`Post created. Mocked platforms: ${response.data.mocked_platforms.join(', ')}`);
      } else {
        toast.success('Post published to all platforms');
      }
      setContent('');
      setSelectedPlatforms([]);
      setSelectedBook('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    }
    setPosting(false);
  };

  const handleSendWhatsApp = async (e) => {
    e.preventDefault();
    if (!whatsappPhone.trim() || !whatsappMessage.trim()) {
      toast.error('Please enter phone number and message');
      return;
    }

    setSendingWhatsApp(true);
    try {
      const response = await axios.post(`${API}/admin/whatsapp/send`, {
        phone_number: whatsappPhone,
        message: whatsappMessage
      }, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success('WhatsApp message sent');
        setWhatsappPhone('');
        setWhatsappMessage('');
      } else {
        toast.error(response.data.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp message');
    }
    setSendingWhatsApp(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-social">
      <h1 className="font-heading text-3xl font-bold mb-8">Social Media & Messaging</h1>

      <Alert className="mb-6 border-yellow-200 bg-yellow-50">
        <AlertCircle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-800">
          Configure API credentials in <strong>Settings → API Credentials</strong> to enable real posting. 
          Posts will be recorded but won't publish without valid credentials.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="social" className="space-y-6">
        <TabsList>
          <TabsTrigger value="social" className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Social Posts
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            WhatsApp
          </TabsTrigger>
        </TabsList>

        {/* Social Posts Tab */}
        <TabsContent value="social">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Create Post */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Share2 className="h-5 w-5" />
                  Create Post
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePost} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Platforms *</Label>
                    <div className="flex flex-wrap gap-2">
                      {PLATFORMS.filter(p => p.id !== 'whatsapp').map((platform) => (
                        <div
                          key={platform.id}
                          className={`flex items-center gap-2 px-4 py-2 border rounded-sm cursor-pointer transition-colors ${
                            selectedPlatforms.includes(platform.id)
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:bg-muted'
                          }`}
                          onClick={() => togglePlatform(platform.id)}
                        >
                          <Checkbox
                            checked={selectedPlatforms.includes(platform.id)}
                            onCheckedChange={() => togglePlatform(platform.id)}
                          />
                          <span className="text-sm font-medium">{platform.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="book">Link Book (optional)</Label>
                    <Select value={selectedBook} onValueChange={(val) => setSelectedBook(val === 'none' ? '' : val)}>
                      <SelectTrigger data-testid="social-book-select">
                        <SelectValue placeholder="Select a book to feature" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {books.map((book) => (
                          <SelectItem key={book.book_id} value={book.book_id}>
                            {book.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="content">Post Content *</Label>
                    <Textarea
                      id="content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Write your post content here..."
                      rows={6}
                      required
                      data-testid="social-content"
                    />
                    <p className="text-xs text-muted-foreground">
                      {content.length}/280 characters
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-lift" 
                    disabled={posting}
                    data-testid="publish-social-btn"
                  >
                    {posting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Publish Post
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Post History */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Post History</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Content</TableHead>
                      <TableHead>Platforms</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {posts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No posts yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      posts.map((post) => (
                        <TableRow key={post.post_id}>
                          <TableCell className="max-w-[200px]">
                            <p className="truncate">{post.content}</p>
                            {post.book_info && (
                              <p className="text-xs text-muted-foreground">
                                📚 {post.book_info.title}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {post.platforms.map((p) => (
                                <Badge key={p} variant="secondary" className="text-xs">
                                  {p}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(post.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Send WhatsApp Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendWhatsApp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={whatsappPhone}
                      onChange={(e) => setWhatsappPhone(e.target.value)}
                      placeholder="+1234567890"
                      required
                      data-testid="whatsapp-phone"
                    />
                    <p className="text-xs text-muted-foreground">
                      Include country code (e.g., +1 for US)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wa-message">Message *</Label>
                    <Textarea
                      id="wa-message"
                      value={whatsappMessage}
                      onChange={(e) => setWhatsappMessage(e.target.value)}
                      placeholder="Type your message here..."
                      rows={4}
                      required
                      data-testid="whatsapp-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-lift" 
                    disabled={sendingWhatsApp}
                    data-testid="send-whatsapp-btn"
                  >
                    {sendingWhatsApp ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send WhatsApp Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl">WhatsApp Setup Guide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  To enable WhatsApp messaging, you need to configure the WhatsApp Business API credentials:
                </p>
                <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
                  <li>Create a Meta Business account</li>
                  <li>Set up WhatsApp Business API in Meta Developer Console</li>
                  <li>Get your Phone Number ID and Access Token</li>
                  <li>Add credentials in Settings → API Credentials</li>
                </ol>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    WhatsApp Business API requires a verified business account and approval from Meta.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
